// reward.js — per-brand reward + identity copy for the customer flow.
//
// Each collector decides what it gives customers for testimonials:
//   brand.reward.type === 'cash'   → built from approval_payout_cents /
//                                    usage_bonus_cents (e.g. Voiply: $10 + $20)
//   brand.reward.type === 'custom' → the RavePilot free-month pitch (defaults
//                                    below, overridable per-field in config)
//   neither set                    → cash if payout cents > 0, else custom.
//
// Also carries brand identity for copy + the consent/license text:
// name ("Voiply") and legal_name ("Voiply, LLC").

export function rewardCopy(collector) {
  const brand = (collector && collector.brand) || {};
  const name = brand.name || 'RavePilot';
  const legalName = brand.legal_name || (name === 'RavePilot' ? 'RavePilot, Inc.' : name);
  const r = brand.reward || {};
  const a = Math.round((collector?.approval_payout_cents ?? 0) / 100);
  const u = Math.round((collector?.usage_bonus_cents ?? 0) / 100);
  const cash = r.type === 'cash' || (r.type !== 'custom' && a > 0);

  if (cash) {
    return {
      type: 'cash', name, legalName, approval: a, usage: u,
      destLabel: r.dest_label || 'Reward going to',
      submitNextReward: 'We email you about your reward once a clip is accepted.',
      welcomeLede: `$${a} for every video we accept${u ? `, and $${u} more if it's used in an ad` : ''}`,
      heroAmount: `$${a}`,
      heroSub: `per accepted video${u ? ` · $${u} bonus if it's used in an ad` : ''}`,
      pickStrong: `$${a} reward`,
      pickAfter: u ? `, plus $${u} more if your video ends up in an ad.` : '.',
      earnStrong: `$${a} per accepted video`,
      earnUp: u ? `+ $${u} if used in an ad` : '',
      cardAmount: `$${a} each`,
      cardSub: `$${a} per video once accepted`,
      submitStrong: `$${a} per accepted clip`,
      submitBonus: u ? `plus $${u} more for each clip used in an ad` : '',
      ftc: `$${a} per accepted video${u ? `, plus $${u} if used in an ad` : ''}`,
      thanksStepTitle: 'Your reward, on its way',
      thanksStepBody: `we email you about your $${a} reward${u ? `, and $${u} more if your video is used in an ad` : ''}`,
      thanksExtra: '',
      thanksClose: "Rewatch what you sent. Closing this page won't affect your reward.",
    };
  }
  return {
    type: 'custom', name, legalName, approval: 0, usage: 0,
    destLabel: r.dest_label || 'Free month goes to',
    submitNextReward: r.submit_next_reward || 'You get an email to activate your free month.',
    welcomeLede: r.welcome_lede || `a free month of ${name}`,
    heroAmount: r.hero_amount || '1 month',
    heroSub: r.hero_sub || `free month of ${name} · plus an invite to be one of our first customers`,
    pickStrong: r.pick_strong || `free month of ${name}`,
    pickAfter: r.pick_after || ', along with an invite to be one of our first customers.',
    earnStrong: r.earn_strong || `1 month of ${name}, free`,
    earnUp: r.earn_up || 'plus a first-customer invite',
    cardAmount: r.card_amount || '1 month free',
    cardSub: r.card_sub || `free month of ${name} once accepted`,
    submitStrong: r.submit_strong || `1 month of ${name}, free`,
    submitBonus: r.submit_bonus || 'plus an invite to be one of our first customers',
    ftc: r.ftc || `a free month of ${name} service`,
    thanksStepTitle: r.thanks_step_title || 'Your free month, activated',
    thanksStepBody: r.thanks_step_body || `we email you to activate a free month of ${name}`,
    thanksExtra: r.thanks_extra || `You'll get an invite to come aboard early and help shape ${name}.`,
    thanksClose: r.thanks_close || "Rewatch what you sent. Closing this page won't affect your free month.",
  };
}
